import { format } from 'date-fns';
import { supabase } from './supabase';
import { sendEmailWithFallback } from './email';
import { parsePayrollPeriodRange } from './payrollPeriodSettings';
import type { PayrollRun, ResourcePayrollData } from './payrollRuns';
import { getPayrollDataForRun } from './payrollRuns';
import { fetchPayrollHoursLabels } from './payrollHoursLabel';

interface SendPayrollBreakdownParams {
  businessId: string;
  accountantEmail: string;
  periodStart: Date;
  periodEnd: Date;
  runNumber: number;
  payrollData: Record<string, ResourcePayrollData>;
}

function buildPayrollSummaryHtml(
  businessName: string,
  periodStart: Date,
  periodEnd: Date,
  runNumber: number,
  payrollData: Record<string, ResourcePayrollData>,
  totalHoursLabel: string,
  overtimeLabel: string,
): string {
  const resources = Object.values(payrollData);
  const totalGross = resources.reduce((sum, r) => sum + r.totalEarnings, 0);
  const totalMinutes = resources.reduce((sum, r) => sum + (r.timeclock?.totalMinutes || 0), 0);
  const totalOvertimeMinutes = resources.reduce(
    (sum, r) => sum + (r.timeclock?.overtimeMinutes || 0), 0);
  const totalOvertimeEarnings = resources.reduce(
    (sum, r) => sum + (r.timeclock?.overtimeEarnings || 0), 0);

  let totalEntries = 0;
  for (const r of resources) {
    totalEntries += r.timeclock.entries.length;
    totalEntries += r.bonuses?.entries?.length || 0;
    Object.values(r.bookings).forEach((booking) => {
      totalEntries += booking.items.length;
    });
  }

  const periodLabel = `${format(periodStart, 'MMM d, yyyy')} – ${format(periodEnd, 'MMM d, yyyy')}`;

  return `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 560px; margin: 0 auto; color: #111827;">
      <div style="background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden;">
        <div style="background: #f8fafc; padding: 24px; border-bottom: 1px solid #e5e7eb;">
          <h1 style="margin: 0 0 8px 0; font-size: 22px; font-weight: 700; color: #111827;">
            ${businessName} payroll is ready to process
          </h1>
          <p style="margin: 0; font-size: 14px; color: #6b7280;">
            Pay period ${periodLabel} · Run #${runNumber}
          </p>
        </div>
        <div style="padding: 24px;">
          <p style="margin: 0 0 20px 0; font-size: 15px; line-height: 1.6; color: #374151;">
            A payroll run has been completed and is ready for your review. A detailed breakdown PDF is attached with each team member's entries, timeclock hours, breaks, and earnings.
          </p>
          <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
            <p style="margin: 0 0 12px 0; font-size: 13px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Summary</p>
            <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
              <tr>
                <td style="padding: 6px 0; color: #6b7280;">Resources</td>
                <td style="padding: 6px 0; text-align: right; font-weight: 600; color: #111827;">${resources.length}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #6b7280;">Total entries</td>
                <td style="padding: 6px 0; text-align: right; font-weight: 600; color: #111827;">${totalEntries}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #6b7280;">Total hours</td>
                <td style="padding: 6px 0; text-align: right; font-weight: 600; color: #111827;">${totalHoursLabel}</td>
              </tr>
              ${totalOvertimeMinutes > 0 ? `
              <tr>
                <td style="padding: 6px 0; color: #6b7280;">Overtime (included)</td>
                <td style="padding: 6px 0; text-align: right; font-weight: 600; color: #ea580c;">${overtimeLabel} · $${totalOvertimeEarnings.toFixed(2)}</td>
              </tr>` : ''}
              <tr>
                <td style="padding: 8px 0 0 0; color: #111827; font-weight: 600; border-top: 1px solid #e5e7eb;">Gross pay</td>
                <td style="padding: 8px 0 0 0; text-align: right; font-weight: 700; font-size: 16px; color: #2563eb; border-top: 1px solid #e5e7eb;">$${totalGross.toFixed(2)}</td>
              </tr>
            </table>
          </div>
          <p style="margin: 0; font-size: 13px; color: #6b7280; line-height: 1.5;">
            Please review the attached PDF for the full itemized breakdown by resource.
          </p>
        </div>
        <div style="background: #f8fafc; padding: 16px 24px; border-top: 1px solid #e5e7eb; text-align: center;">
          <p style="margin: 0; font-size: 12px; color: #9ca3af;">Sent from ${businessName}</p>
        </div>
      </div>
    </div>
  `;
}

export async function generatePayrollBreakdownPdf({
  businessId,
  periodStart,
  periodEnd,
  runNumber,
  payrollData,
}: Omit<SendPayrollBreakdownParams, 'accountantEmail'>): Promise<
  { success: true; base64: string; filename: string; businessName: string } | { success: false; error: string }
> {
  if (!supabase) {
    return { success: false, error: 'Supabase client is not initialized' };
  }

  const { data: business, error: businessError } = await supabase
    .from('businesses')
    .select('name, timezone')
    .eq('id', businessId)
    .single();

  if (businessError || !business?.name) {
    return { success: false, error: 'Could not load business details' };
  }

  const timeZone = business.timezone?.trim() || 'America/Vancouver';
  const periodStartStr = format(periodStart, 'yyyy-MM-dd');
  const periodEndStr = format(periodEnd, 'yyyy-MM-dd');

  const { data: pdfResult, error: pdfError } = await supabase.functions.invoke(
    'generate-payroll-breakdown-pdf',
    {
      body: {
        businessId,
        businessName: business.name,
        periodStart: periodStartStr,
        periodEnd: periodEndStr,
        runNumber,
        timeZone,
        resources: payrollData,
      },
    },
  );

  if (pdfError || !pdfResult?.base64) {
    console.error('Payroll PDF generation failed:', pdfError || pdfResult?.error);
    return {
      success: false,
      error: pdfResult?.error || pdfError?.message || 'Failed to generate payroll PDF',
    };
  }

  return {
    success: true,
    base64: pdfResult.base64,
    filename: pdfResult.filename || `payroll-breakdown-${periodStartStr}.pdf`,
    businessName: business.name,
  };
}

async function saveBase64Pdf(base64: string, filename: string): Promise<'saved' | 'cancelled'> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });

  // iOS WKWebView ignores <a download>. Invoices open the share sheet instead.
  const { Capacitor } = await import('@capacitor/core');
  if (Capacitor.isNativePlatform()) {
    const file = new File([blob], filename, { type: 'application/pdf' });
    if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: filename });
        return 'saved';
      } catch (err) {
        if ((err as Error).name === 'AbortError') return 'cancelled';
        throw err;
      }
    }
    throw new Error('Could not open the share sheet to save this PDF');
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  return 'saved';
}

export async function sendPayrollBreakdownToAccountant({
  businessId,
  accountantEmail,
  periodStart,
  periodEnd,
  runNumber,
  payrollData,
}: SendPayrollBreakdownParams): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      return { success: false, error: 'Supabase client is not initialized' };
    }

    const pdf = await generatePayrollBreakdownPdf({
      businessId,
      periodStart,
      periodEnd,
      runNumber,
      payrollData,
    });

    if (!pdf.success) {
      return { success: false, error: pdf.error };
    }

    const businessName = pdf.businessName;
    const pdfFilename = pdf.filename;
    const periodLabel = `${format(periodStart, 'MMM d, yyyy')} – ${format(periodEnd, 'MMM d, yyyy')}`;
    const resources = Object.values(payrollData);
    const totalMinutes = resources.reduce((sum, resource) => sum + (resource.timeclock?.totalMinutes || 0), 0);
    const totalOvertimeMinutes = resources.reduce((sum, resource) => sum + (resource.timeclock?.overtimeMinutes || 0), 0);
    const hourLabels = await fetchPayrollHoursLabels(businessId, [totalMinutes, totalOvertimeMinutes]);
    const emailBody = buildPayrollSummaryHtml(
      businessName,
      periodStart,
      periodEnd,
      runNumber,
      payrollData,
      hourLabels[String(totalMinutes)] || '',
      hourLabels[String(totalOvertimeMinutes)] || '',
    );

    const emailResult = await sendEmailWithFallback({
      businessId,
      to: accountantEmail,
      subject: `${businessName} payroll is ready to process (${periodLabel})`,
      body: emailBody,
      attachments: [
        {
          filename: pdfFilename,
          content: pdf.base64,
          type: 'application/pdf',
        },
      ],
    });

    if (!emailResult.success) {
      return { success: false, error: emailResult.error || 'Failed to send email to accountant' };
    }

    return { success: true };
  } catch (err) {
    console.error('Error sending payroll breakdown to accountant:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to send payroll breakdown',
    };
  }
}

/** Download the same accountant breakdown PDF for a past payroll run. */
export async function downloadPayrollBreakdownForRun(
  businessId: string,
  run: Pick<PayrollRun, 'id' | 'run_number' | 'period_start_date' | 'period_end_date'>,
): Promise<{ success: boolean; error?: string; cancelled?: boolean }> {
  try {
    const payrollData = await getPayrollDataForRun(run.id);
    if (Object.keys(payrollData).length === 0) {
      return { success: false, error: 'No payroll entries found for this run' };
    }

    const { start, end } = parsePayrollPeriodRange(run.period_start_date, run.period_end_date);
    const pdf = await generatePayrollBreakdownPdf({
      businessId,
      periodStart: start,
      periodEnd: end,
      runNumber: run.run_number,
      payrollData,
    });

    if (!pdf.success) {
      return { success: false, error: pdf.error };
    }

    const saved = await saveBase64Pdf(pdf.base64, pdf.filename);
    if (saved === 'cancelled') return { success: true, cancelled: true };
    return { success: true };
  } catch (err) {
    console.error('Error downloading payroll breakdown:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to download payroll breakdown',
    };
  }
}

/** Resend breakdown PDF + email for a past payroll run from history. */
export async function resendPayrollBreakdownForRun(
  businessId: string,
  accountantEmail: string,
  run: Pick<PayrollRun, 'id' | 'run_number' | 'period_start_date' | 'period_end_date'>,
): Promise<{ success: boolean; error?: string }> {
  try {
    const payrollData = await getPayrollDataForRun(run.id);
    const resourceCount = Object.keys(payrollData).length;

    if (resourceCount === 0) {
      return { success: false, error: 'No payroll entries found for this run' };
    }

    const { start, end } = parsePayrollPeriodRange(run.period_start_date, run.period_end_date);

    return sendPayrollBreakdownToAccountant({
      businessId,
      accountantEmail,
      periodStart: start,
      periodEnd: end,
      runNumber: run.run_number,
      payrollData,
    });
  } catch (err) {
    console.error('Error resending payroll breakdown:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to resend payroll breakdown',
    };
  }
}
